import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Copy, RefreshCw, CalendarDays, MessageCircle, CreditCard } from "lucide-react";
import { api, API, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useT } from "../lib/i18n";
import { applyBranding } from "../lib/helpers";
import { PageHeader, Field, NativeSelect } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { WhatsAppSettings, RemindersSettings } from "../components/WhatsAppSettings";
import { WebsiteSettings } from "../components/WebsiteSettings";
import { ContactSettings } from "../components/ContactButtons";
import { TemplateSettings } from "../components/TemplateSettings";
import { InvoicingSettings } from "../components/InvoicingSettings";
import { SupportAccess } from "../components/AccountSwitch";
import { AutoLogo, LightLogo } from "../components/AppLogo";
import { PaymentSettings } from "../components/PaymentSettings";

const PRESETS = [
  { name: "Rose Pink", primary: "#e6007e", accent: "#4c6066" },
  { name: "Sage & Slate", primary: "#346f5e", accent: "#d9a441" },
  { name: "Mineral Blue", primary: "#22628f", accent: "#e8673a" },
  { name: "Terracotta", primary: "#a65632", accent: "#dcae3e" },
  { name: "Obsidian", primary: "#3a2f52", accent: "#ecc046" },
];

function resizeLogo(file) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const s = Math.min(1, 320 / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = img.width * s; c.height = img.height * s;
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/png"));
    };
    img.src = URL.createObjectURL(file);
  });
}

function Branding({ b, setB }) {
  const { t } = useT();
  useEffect(() => { applyBranding(b); }, [b]);
  const set = (k) => (v) => setB({ ...b, [k]: v });
  return (
    <div className="grid lg:grid-cols-[1fr_340px] gap-8">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label={t("clinic_name")} className="sm:col-span-2"><Input data-testid="branding-name-input" value={b.clinic_name || ""} onChange={(e) => set("clinic_name")(e.target.value)} /></Field>
        <Field label={t("logo")} className="sm:col-span-2">
          <div className="flex items-center gap-4">
            {b.logo ? <AutoLogo src={b.logo} alt="logo" className="h-12 max-w-[180px]" testId="branding-logo-thumb" /> : <div className="h-14 w-14 rounded-md border border-dashed" />}
            <label className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm cursor-pointer hover:bg-muted">
              <Upload className="h-4 w-4" />Upload
              <input type="file" accept="image/*" className="hidden" data-testid="branding-logo-input" onChange={async (e) => e.target.files[0] && set("logo")(await resizeLogo(e.target.files[0]))} />
            </label>
            {b.logo && <button className="text-xs text-muted-foreground" data-testid="branding-logo-remove" onClick={() => set("logo")("")}>{t("delete")}</button>}
          </div>
        </Field>
        <Field label={t("primary_color")}><Input type="color" className="h-11 p-1" data-testid="branding-primary-input" value={b.primary} onChange={(e) => set("primary")(e.target.value)} /></Field>
        <Field label={t("accent_color")}><Input type="color" className="h-11 p-1" data-testid="branding-accent-input" value={b.accent} onChange={(e) => set("accent")(e.target.value)} /></Field>
        <div className="sm:col-span-2 flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button key={p.name} data-testid={`branding-preset-${p.name.split(" ")[0].toLowerCase()}`} onClick={() => setB({ ...b, primary: p.primary, accent: p.accent })}
              className="flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs hover:bg-muted">
              <span className="h-3 w-3 rounded-full" style={{ background: p.primary }} /><span className="h-3 w-3 rounded-full" style={{ background: p.accent }} />{p.name}
            </button>
          ))}
        </div>
        <Field label={t("sidebar_style")}><NativeSelect testId="branding-sidebar-select" value={b.sidebar_style} onChange={set("sidebar_style")} options={[{ value: "dark", label: "Dark" }, { value: "light", label: "Light" }, { value: "brand", label: "Brand colour" }]} /></Field>
        <Field label={t("layout")}><NativeSelect testId="branding-layout-select" value={b.layout} onChange={set("layout")} options={[{ value: "sidebar", label: "Sidebar" }, { value: "topbar", label: "Top bar" }]} /></Field>
        <Field label={`${t("radius")} (${b.radius}rem)`} className="sm:col-span-2">
          <Slider data-testid="branding-radius-slider" min={0} max={1.5} step={0.125} value={[b.radius ?? 0.75]} onValueChange={([v]) => set("radius")(v)} />
        </Field>
      </div>
      <div className="rounded-[var(--radius)] border overflow-hidden" data-testid="branding-preview">
        <div className="sidebar p-4 flex items-center gap-3">
          {b.logo ? ((b.sidebar_style || "dark") !== "light" ? <LightLogo src={b.logo} className="h-9 w-auto max-w-[160px] object-contain" testId="branding-preview-logo" />
            : <img src={b.logo} alt="" data-testid="branding-preview-logo" className="h-9 w-auto max-w-[160px] object-contain" />) : <div className="h-8 w-8 rounded bg-[hsl(var(--brand-accent))]" />}
          <span className="font-display font-bold">{b.clinic_name}</span>
        </div>
        <div className="p-5 space-y-3 bg-card">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Preview</p>
          <p className="font-display text-xl font-bold">10:30 · Treatment</p>
          <div className="flex gap-2"><Button className="rounded-full" size="sm">Primary</Button>
            <span className="rounded-full px-3 py-1.5 text-xs font-medium bg-[hsl(var(--brand-accent))] text-black/80">Accent</span></div>
        </div>
      </div>
    </div>
  );
}

function CalendarSync({ token, setToken }) {
  const { locations, staff } = useApp();
  const { t } = useT();
  const [loc, setLoc] = useState("");
  const [pro, setPro] = useState("");
  const url = token ? `${API}/calendar/${token}.ics${loc || pro ? "?" : ""}${loc ? `location_id=${loc}` : ""}${loc && pro ? "&" : ""}${pro ? `professional_id=${pro}` : ""}` : "";
  const regen = async () => {
    if (!window.confirm("Old calendar links will stop working. Continue?")) return;
    const { data } = await api.post("/settings/calendar-token");
    setToken(data.calendar_token);
  };
  return (
    <div className="max-w-2xl space-y-5">
      <div className="flex items-start gap-3 text-sm text-muted-foreground"><CalendarDays className="h-5 w-5 text-primary shrink-0" />
        <p>Copy the link below, then in Google Calendar go to <b>Other calendars → + → From URL</b> and paste it. Appointments will appear automatically (Google refreshes subscribed calendars every few hours).</p></div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t("location")}><NativeSelect testId="calendar-location-select" value={loc} onChange={setLoc} options={[{ value: "", label: t("all_locations") }, ...locations.map((l) => ({ value: l.id, label: l.name }))]} /></Field>
        <Field label={t("professional")}><NativeSelect testId="calendar-pro-select" value={pro} onChange={setPro} options={[{ value: "", label: "—" }, ...staff.map((s) => ({ value: s.id, label: s.name }))]} /></Field>
      </div>
      <div className="flex gap-2">
        <Input readOnly value={url} data-testid="calendar-feed-url" className="font-mono text-xs" />
        <Button variant="outline" data-testid="calendar-copy-btn" onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copied"); }}><Copy className="h-4 w-4" /></Button>
      </div>
      <Button variant="ghost" size="sm" data-testid="calendar-regen-btn" onClick={regen}><RefreshCw className="h-4 w-4 mr-1.5" />Regenerate secret link</Button>
    </div>
  );
}

function Audit() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/audit").then((r) => setRows(r.data)); }, []);
  return (
    <div className="rounded-[var(--radius)] border bg-card divide-y text-sm max-h-[60vh] overflow-y-auto" data-testid="audit-list">
      {rows.map((r) => (
        <div key={r.id} className="flex flex-wrap gap-4 px-4 py-2.5">
          <span className="font-mono text-xs w-44">{new Date(r.created_at).toLocaleString()}</span>
          <span className="w-32 font-medium">{r.user_name}</span><span className="flex-1">{r.action} · {r.entity}</span>
        </div>
      ))}
    </div>
  );
}

export default function SettingsPage() {
  const { user, settings, setSettings, reload } = useApp();
  const { t } = useT();
  const [b, setB] = useState(null);
  const [booking, setBooking] = useState({});
  const [reminders, setReminders] = useState({});
  const [defLang, setDefLang] = useState("en");
  const [token, setToken] = useState("");

  useEffect(() => {
    if (settings.branding && !b && settings.clinic_id === user?.clinic_id) { setB(settings.branding); setBooking(settings.booking || {}); setReminders(settings.reminders || {}); setDefLang(settings.default_language || "en"); setToken(settings.calendar_token || ""); }
  }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => applyBranding(settings.branding), [settings.branding]);

  const save = async () => {
    try {
      const body = { clinic_id: settings.clinic_id, branding: b, default_language: defLang, reminders,
        booking: Object.fromEntries(Object.entries(booking).map(([k, v]) => [k, Number(v)])) };
      const { data } = await api.put("/settings", body);
      setSettings(data); toast.success("Settings saved"); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!b) return null;

  return (
    <div>
      <PageHeader title={t("settings")}><Button className="rounded-full" data-testid="settings-save-btn" onClick={save}>{t("save")}</Button></PageHeader>
      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "branding"}>
        <TabsList className="flex-wrap h-auto mb-6">
          <TabsTrigger value="branding" data-testid="settings-tab-branding">{t("branding")}</TabsTrigger>
          <TabsTrigger value="booking" data-testid="settings-tab-booking">{t("booking_rules")}</TabsTrigger>
          <TabsTrigger value="website" data-testid="settings-tab-website">Website & Google</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="settings-tab-reminders">{t("reminders")}</TabsTrigger>
          <TabsTrigger value="contact" data-testid="settings-tab-contact">Contact</TabsTrigger>
          <TabsTrigger value="payments" data-testid="settings-tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="settings-tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="templates" data-testid="settings-tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="settings-tab-whatsapp">WhatsApp</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="settings-tab-calendar">{t("calendar_sync")}</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="settings-tab-integrations">Integrations</TabsTrigger>
          <TabsTrigger value="audit" data-testid="settings-tab-audit">{t("audit")}</TabsTrigger>
        </TabsList>
        <TabsContent value="branding"><Branding b={b} setB={setB} /></TabsContent>
        <TabsContent value="booking">
          <div className="grid sm:grid-cols-2 gap-5 max-w-2xl">
            {[["min_lead_hours", "Minimum notice for online booking (hours)"], ["cancel_hours", "Free cancellation up to (hours before)"],
              ["slot_step", "Time slot interval (minutes)"], ["max_days_ahead", "Bookable up to (days ahead)"]].map(([k, label]) => (
              <Field key={k} label={label}><Input type="number" min="0" data-testid={`booking-${k}-input`} value={booking[k] ?? ""} onChange={(e) => setBooking({ ...booking, [k]: e.target.value })} /></Field>
            ))}
            <Field label={`${t("language")} (default)`}><NativeSelect testId="default-language-select" value={defLang} onChange={setDefLang} options={[{ value: "en", label: "English" }, { value: "it", label: "Italiano" }]} /></Field>
          </div>
        </TabsContent>
        <TabsContent value="website"><WebsiteSettings /></TabsContent>
        <TabsContent value="reminders"><RemindersSettings value={reminders} onChange={setReminders} whatsappConnected={settings.whatsapp_connected} /></TabsContent>
        <TabsContent value="contact"><ContactSettings /></TabsContent>
        <TabsContent value="templates"><TemplateSettings /></TabsContent>
        <TabsContent value="whatsapp"><WhatsAppSettings onChanged={reload} /></TabsContent>
        <TabsContent value="calendar"><CalendarSync token={token} setToken={setToken} /></TabsContent>
        <TabsContent value="integrations">
          <div className="grid md:grid-cols-3 gap-4">
            {[[CreditCard, "Stripe", "Online card payments for bookings, packages, gift cards and the shop. Connect your own Stripe account in the Payments tab."],
              [MessageCircle, "WhatsApp", settings.whatsapp_connected ? "Connected: confirmations and reminders are sent automatically to opted-in clients." : "Not connected: the WhatsApp button opens a pre-filled chat. Connect your WhatsApp Business account in the WhatsApp tab to send automatically."],
              [CreditCard, "SumUp", "Payment links, QR codes and SumUp Solo card reader payments from any appointment. Connect your SumUp account in the Payments tab."]].map(([Icon, name, text]) => (
              <div key={name} className="rounded-[var(--radius)] border bg-card p-5" data-testid={`integration-${name.toLowerCase()}`}>
                <Icon className="h-5 w-5 text-primary mb-3" /><div className="font-semibold mb-1">{name}</div><p className="text-sm text-muted-foreground">{text}</p>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="audit"><SupportAccess /><Audit /></TabsContent>
        <TabsContent value="payments"><PaymentSettings /></TabsContent>
        <TabsContent value="invoices"><InvoicingSettings /></TabsContent>
      </Tabs>
    </div>
  );
}
